import React from 'react';
import { useParams } from 'react-router-dom';
import { Clock, Users, Award, Check, Play, Download, Brain } from 'lucide-react';
import { getFormationById } from './data/products';

const CourseDetail = () => {
  const { courseId } = useParams();
  const course = getFormationById(courseId);

  if (!course) {
    return (
      <div className="min-h-screen text-white pt-20 md:pt-32">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-2xl md:text-3xl font-bold mb-4">Formation non trouvée</h1>
            <p className="text-sm md:text-base text-white/70">
              Désolé, la formation que vous recherchez n'existe pas ou n'est pas disponible.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white pt-20 md:pt-32">
      {/* Hero Section - Responsive */}
      <div className="container mx-auto px-4 mb-8 md:mb-16">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-12">
          <div className="md:col-span-2">
            <div className="inline-block px-4 py-2 rounded-full bg-[#017D7E]/10 text-[#017D7E] mb-6 md:mb-8 text-sm md:text-base">
              Formation Certifiante
            </div>
            <h1 className="text-3xl md:text-5xl font-bold mb-4 md:mb-6">
              {course.title}
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#017D7E] to-[#00A99D]">
                {course.hero.subtitle}
              </span>
            </h1>
            <p className="text-base md:text-xl text-white/70 mb-6 md:mb-8">
              {course.hero.description}
            </p>
            
            <div className="flex flex-wrap gap-4 md:gap-6 mb-8 md:mb-12">
              <div className="flex items-center gap-2 text-sm md:text-base">
                <Clock className="w-4 h-4 md:w-5 md:h-5 text-[#017D7E]" />
                <span>{course.duration}</span>
              </div>
              {course.students && (
                <div className="flex items-center gap-2 text-sm md:text-base">
                  <Users className="w-4 h-4 md:w-5 md:h-5 text-[#017D7E]" />
                  <span>{course.students} élèves</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm md:text-base">
                <Award className="w-4 h-4 md:w-5 md:h-5 text-[#017D7E]" />
                <span>Certification incluse</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button className="w-full sm:w-auto px-6 md:px-8 py-3 md:py-4 rounded-lg bg-gradient-to-r from-green-500 to-[#017D7E] hover:from-green-600 hover:to-emerald-600 text-white font-medium transition-all text-sm md:text-base">
                Commencer la formation
              </button>
              <button className="w-full sm:w-auto px-6 md:px-8 py-3 md:py-4 rounded-lg border border-green-500/50 hover:bg-[#017D7E]/10 text-white font-medium transition-all text-sm md:text-base">
                Télécharger le programme
              </button>
            </div>
          </div>

          <div className="mt-6 md:mt-0">
            <div className="bg-white/5 rounded-xl border border-white/10 p-4 md:p-6">
              <h3 className="text-lg md:text-xl font-bold mb-4">Cette formation inclut :</h3>
              <ul className="space-y-3">
                <ResourceItem icon={Play} text={course.resources.videos} />
                <ResourceItem icon={Download} text={course.resources.downloads} />
                <ResourceItem icon={Brain} text={course.resources.exercises} />
                <ResourceItem icon={Award} text={course.certification.type} />
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Content - Responsive */}
      <div className="container mx-auto px-4 mb-8 md:mb-16">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-12">
          <div className="md:col-span-2">
            {/* Programme */}
            <div className="bg-white/5 rounded-xl border border-white/10 p-4 md:p-8 mb-6 md:mb-12">
              <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6">Programme de la formation</h2>
              <div className="space-y-4">
                {course.program.modules.map((module, moduleIndex) => (
                  <ModuleSection 
                    key={moduleIndex}
                    module={module}
                  />
                ))}
              </div>
            </div>

            
          
          </div>
        </div>
      </div>
    </div>
  );
};

// Composants utilitaires
const ResourceItem = ({ icon: Icon, text }) => (
  <li className="flex items-center gap-2 text-sm md:text-base text-white/70">
    <Icon className="w-4 h-4 md:w-5 md:h-5 text-[#017D7E] flex-shrink-0" />
    <span>{text}</span>
  </li>
);

const ModuleSection = ({ module }) => (
  <div className="bg-white/5 rounded-lg p-3 md:p-4">
    <div className="flex flex-col md:flex-row md:items-center justify-between mb-3 md:mb-4">
      <h3 className="text-base md:text-lg font-semibold">{module.title}</h3>
      <span className="text-sm md:text-base text-white/70">{module.duration}</span>
    </div>
    {module.chapters.map((chapter, chapterIndex) => (
      <div key={chapterIndex} className="mt-3 md:mt-4">
        <h4 className="text-sm md:text-base font-medium mb-2">{chapter.title}</h4>
        <ul className="space-y-2">
          {chapter.content.map((item, itemIndex) => (
            <li key={itemIndex} className="flex items-start gap-2 text-sm md:text-base text-white/70">
              <Check className="w-4 h-4 text-[#017D7E] mt-0.5 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    ))}
  </div>
);

export default CourseDetail;